import { useState } from "react";
import { usePresence } from "@/context/PresenceContext";
import { useAuth } from "@/context/AuthContext";
import { Users, ChevronUp } from "lucide-react";

function initials(name) {
  return (name || "?").trim().slice(0, 2).toUpperCase();
}
function hue(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
  return h;
}

// A small always-visible "who's on this page" pill, next to the chat button.
export function PagePresence() {
  const { user } = useAuth();
  const { count, users, label } = usePresence();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  return (
    <div className="fixed bottom-5 left-24 z-40">
      {open && (
        <div data-testid="presence-roster"
          className="absolute bottom-12 left-0 w-60 max-w-[calc(100vw-7rem)] rounded-2xl border border-white/10 bg-[rgba(14,18,28,0.92)] backdrop-blur-md shadow-2xl p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300/80 mb-2">
            {count} {count === 1 ? "person" : "people"} in {label}
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {users.length === 0 ? (
              <div className="text-white/35 text-xs font-mono py-2">Just you for now.</div>
            ) : (
              users.map((u) => (
                <div key={u.id} data-testid={`presence-user-${u.id}`} className="flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-bold text-black shrink-0"
                    style={{ backgroundColor: `hsl(${hue(u.name)} 70% 65%)` }}>
                    {initials(u.name)}
                  </span>
                  <span className="text-white/80 text-xs truncate flex-1">{u.name}</span>
                  {u.role === "admin" && <span className="text-[8px] font-mono bg-amber-300 text-black px-1 rounded">ADMIN</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      <button
        data-testid="presence-toggle"
        onClick={() => setOpen((o) => !o)}
        className="h-10 pl-2.5 pr-3 rounded-full bg-[rgba(14,18,28,0.85)] border border-emerald-400/30 backdrop-blur-md shadow-lg flex items-center gap-2 hover:border-emerald-400/60 transition-colors"
        title={`${count} here in ${label}`}
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
        </span>
        <Users className="h-4 w-4 text-emerald-300" />
        <span className="font-mono text-xs text-white tabular-nums" data-testid="presence-count">{count}</span>
        <ChevronUp className={`h-3 w-3 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
}
